import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  CreditCard, Package, CheckCircle2, Crown, ArrowUpRight, Clock,
  AlertTriangle, Check, ShoppingCart, Bot, MapPin, Plus, Minus,
  Loader2, X, Shield, Receipt, Percent, ChevronRight, Sparkles,
  Pencil, Building2, Save, FileText, Download, ExternalLink, Lock, Tag
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

// ─── METODO DI PAGAMENTO ────────────────────────────────────────────────────
const brandLabels = { visa: "Visa", mastercard: "Mastercard", amex: "Amex", discover: "Discover" };

const PaymentMethodSection = ({ token, refreshTrigger }) => {
  const [pm, setPm] = useState(null);
  const [loadingPm, setLoadingPm] = useState(true);
  const [changingCard, setChangingCard] = useState(false);

  const fetchPm = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/payment-method`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setPm(await res.json());
    } catch { /* ignore */ }
    setLoadingPm(false);
  };

  useEffect(() => { fetchPm(); }, [token, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeCard = async () => {
    setChangingCard(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/setup-payment-method`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          success_url: window.location.href,
          cancel_url: window.location.href,
        }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.setup_url) { window.location.href = d.setup_url; return; }
      }
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Impossibile aprire il portale di pagamento");
    } catch { toast.error("Errore di connessione"); }
    setChangingCard(false);
  };

  return (
    <div className="overflow-hidden" data-testid="payment-method-section">
      {loadingPm ? (
        <div className="flex items-center justify-center py-3 px-4">
          <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
        </div>
      ) : pm?.has_payment_method ? (
        <div className="flex items-center justify-between px-4 py-3.5 gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-6 rounded bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-[8px] font-black tracking-wider uppercase">
                {brandLabels[pm.brand] || pm.brand}
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 tracking-widest leading-tight">
                •••• {pm.last4}
              </p>
              <p className="text-[10px] text-slate-400">Scade {String(pm.exp_month).padStart(2,"0")}/{pm.exp_year}</p>
            </div>
          </div>
          <button
            onClick={handleChangeCard}
            disabled={changingCard}
            className="text-indigo-600 hover:text-indigo-700 active:scale-95 text-xs font-semibold transition-all flex-shrink-0 flex items-center gap-1"
            data-testid="change-card-btn"
          >
            {changingCard ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />}
            {changingCard ? "Apertura..." : "Cambia carta"}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between px-4 py-3.5 gap-3">
          <div className="flex items-center gap-3">
            <CreditCard className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <p className="text-sm text-slate-500">Nessun metodo di pagamento</p>
          </div>
          <button
            onClick={handleChangeCard}
            disabled={changingCard}
            className="text-indigo-600 hover:text-indigo-700 active:scale-95 text-xs font-semibold transition-all flex-shrink-0 flex items-center gap-1"
            data-testid="add-card-btn"
          >
            {changingCard ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />}
            {changingCard ? "Apertura..." : "Aggiungi carta"}
          </button>
        </div>
      )}
    </div>
  );
};


// ─── DATI FATTURAZIONE ───────────────────────────────────────────────────────
const BillingInfoSection = ({ token }) => {
  const [info, setInfo] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchInfo = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/billing-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setInfo(d);
        setForm(d);
      }
    } catch {}
  };

  useEffect(() => { fetchInfo(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/billing-info`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setInfo({ ...form });
        setEditing(false);
        toast.success("Dati di fatturazione salvati");
      } else {
        toast.error("Errore nel salvataggio");
      }
    } catch { toast.error("Errore di connessione"); }
    setSaving(false);
  };

  const lockedField = (label, key) => (
    <div key={key}>
      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-1">
        <Lock className="h-3 w-3" /> {label}
      </label>
      <div className="h-9 flex items-center px-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-500 select-none">
        {form[key] || <span className="text-slate-300">—</span>}
      </div>
    </div>
  );

  const field = (label, key, placeholder = "") => (
    <div key={key}>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">{label}</label>
      <Input
        value={form[key] || ""}
        onChange={(e) => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className="h-9 text-sm rounded-xl border-slate-200"
        data-testid={`billing-field-${key}`}
      />
    </div>
  );

  return (
    <div className="overflow-hidden" data-testid="billing-info-section">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
        data-testid="toggle-billing-info"
      >
        <div className="flex items-center gap-3 text-left">
          <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-slate-900">Dati di Fatturazione</p>
            <p className="text-[11px] text-slate-400 truncate max-w-[230px]">
              {info?.ragione_sociale || "Nessuna ragione sociale impostata"}
              {info?.partita_iva ? ` — ${info.partita_iva}` : ""}
            </p>
          </div>
        </div>
        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform duration-200 flex-shrink-0 ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-4 space-y-4">
          {!editing ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {[
                  ["Ragione Sociale", info?.ragione_sociale],
                  ["Partita IVA", info?.partita_iva],
                  ["Codice Fiscale", info?.codice_fiscale],
                  ["Indirizzo", info?.indirizzo],
                  ["CAP", info?.cap],
                  ["Città", info?.citta],
                  ["Provincia", info?.provincia],
                  ["PEC", info?.pec],
                  ["Codice SDI", info?.codice_sdi],
                ].map(([label, val]) => val ? (
                  <div key={label}>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
                    <p className="text-sm text-slate-800 font-medium">{val}</p>
                  </div>
                ) : null)}
              </div>
              <Button
                onClick={() => setEditing(true)}
                size="sm"
                variant="outline"
                className="rounded-xl text-xs font-semibold gap-1.5"
                data-testid="edit-billing-btn"
              >
                <Pencil className="h-3.5 w-3.5" /> Modifica
              </Button>
            </>
          ) : (
            <div className="space-y-3">
              {/* Campi bloccati — dati societari legali */}
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                <div className="flex items-start gap-2 mb-3">
                  <Lock className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700 font-medium">
                    I dati societari non sono modificabili perché parte del contratto firmato.
                    Per cambiarli scrivi a <span className="font-bold">info@yollo.me</span>
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {lockedField("Ragione Sociale", "ragione_sociale")}
                  {lockedField("Partita IVA", "partita_iva")}
                  {lockedField("Codice Fiscale", "codice_fiscale")}
                </div>
              </div>

              {/* Campi modificabili */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {field("Indirizzo", "indirizzo", "Via Roma, 1")}
                {field("CAP", "cap", "00100")}
                {field("Città", "citta", "Roma")}
                {field("Provincia", "provincia", "RM")}
                {field("PEC", "pec", "fatture@pec.example.it")}
                {field("Codice SDI", "codice_sdi", "0000000")}
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  size="sm"
                  className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold gap-1.5"
                  data-testid="save-billing-btn"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  {saving ? "Salvataggio..." : "Salva"}
                </Button>
                <Button
                  onClick={() => { setForm(info || {}); setEditing(false); }}
                  size="sm"
                  variant="outline"
                  className="rounded-xl text-xs font-semibold"
                  data-testid="cancel-billing-btn"
                >
                  <X className="h-3.5 w-3.5 mr-1" /> Annulla
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ─── STORICO FATTURE ─────────────────────────────────────────────────────────



const StoricoSection = ({ token }) => {
  const [invoices, setInvoices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [expandedInv, setExpandedInv] = useState(null);

  const toggle = async () => {
    if (!loaded) {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setInvoices(await res.json());
      } catch {}
      setLoaded(true);
    }
    setExpanded(e => !e);
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  const isPaid = (inv) => inv.status === "paid" || inv.status === "pagato";

  return (
    <div className="overflow-hidden" data-testid="storico-section">
      <button onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
        data-testid="toggle-storico">
        <div className="flex items-center gap-3 text-left">
          <Receipt className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-slate-900">Storico Pagamenti</p>
            <p className="text-[11px] text-slate-400">Transazioni e fatture</p>
          </div>
        </div>
        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform duration-200 flex-shrink-0 ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3">
          {!loaded ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
            </div>
          ) : invoices.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nessun pagamento registrato</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {invoices.map((inv, i) => (
                <div key={inv.id} data-testid={`storico-row-${i}`}>
                  <div
                    className={`flex items-center justify-between py-3 gap-3 first:pt-0 ${inv.tipo === "acquisto_mid_cycle" ? "cursor-pointer" : ""}`}
                    onClick={() => inv.tipo === "acquisto_mid_cycle" && setExpandedInv(expandedInv === inv.id ? null : inv.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-bold text-slate-900">
                          {inv.amount_paid > 0 ? `${inv.amount_paid.toFixed(2)}€ IVA inclusa` : "—"}
                        </span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                          isPaid(inv) ? "bg-emerald-50 text-emerald-700" :
                          inv.status === "open" ? "bg-amber-50 text-amber-700" :
                          "bg-slate-100 text-slate-500"
                        }`}>
                          {isPaid(inv) ? "Pagato" : inv.status === "open" ? "In attesa" : inv.status}
                        </span>
                        {inv.tipo === "acquisto_mid_cycle" && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
                            Aggiunta servizi
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {formatDate(inv.created)}{inv.number ? ` · ${inv.number}` : ""}
                        {inv.descrizione && inv.tipo === "acquisto_mid_cycle" && (
                          <span className="ml-1 text-slate-500 font-medium">{inv.descrizione}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {inv.tipo === "acquisto_mid_cycle" && (
                        <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${expandedInv === inv.id ? "rotate-90" : ""}`} />
                      )}
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-indigo-50 hover:border-indigo-200 active:scale-95 transition-all"
                          title="Scarica PDF" data-testid={`download-pdf-${i}`}>
                          <Download className="h-3.5 w-3.5 text-slate-500" />
                        </a>
                      )}
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 active:scale-95 transition-all"
                          title="Visualizza fattura" data-testid={`view-invoice-${i}`}>
                          <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                        </a>
                      )}
                    </div>
                  </div>
                  {/* Dettaglio voci per aggiunta servizi */}
                  {expandedInv === inv.id && inv.voci?.length > 0 && (
                    <div className="mb-3 ml-2 pl-3 border-l-2 border-indigo-100 space-y-1.5">
                      {inv.voci.map((v, vi) => (
                        <div key={vi} className="flex justify-between text-[11px] text-slate-600">
                          <span>{v.descrizione}</span>
                          <span className="font-semibold">{v.importo_netto?.toFixed(2)}€ + IVA</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-[11px] text-slate-400 pt-1 border-t border-slate-100">
                        <span>IVA ({inv.iva_percentuale}%)</span>
                        <span>{inv.iva_importo?.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between text-xs font-bold text-slate-800">
                        <span>Totale pagato</span>
                        <span>{inv.amount_paid?.toFixed(2)}€</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const API = process.env.REACT_APP_BACKEND_URL;

const AbbonamentoPage = () => {
  const { user } = useAuth();
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState(null);
  const [pricingSummary, setPricingSummary] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [selectedPkgs, setSelectedPkgs] = useState(new Set());
  const [addonTelegramQty, setAddonTelegramQty] = useState(0);
  const [upgradeCodice, setUpgradeCodice] = useState("");
  const [addonLocaleExtra, setAddonLocaleExtra] = useState(0);
  const [cart, setCart] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [upgradePending, setUpgradePending] = useState(null);
  const [upgradeQuote, setUpgradeQuote] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [renewalInfo, setRenewalInfo] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelReasonKey, setCancelReasonKey] = useState("");
  const [cancelReasonNote, setCancelReasonNote] = useState("");
  const [cancelMode, setCancelMode] = useState("fine_contratto");
  const [highlightedPkgId, setHighlightedPkgId] = useState(null);
  const [trialEndInfo, setTrialEndInfo] = useState(null);
  const [trialModalDismissed, setTrialModalDismissed] = useState(false);
  const [trialChoiceLoading, setTrialChoiceLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const upgradeStorageKey = `yollo-upgrade:${user?.societa_id || "unselected"}`;

  // ─── Load subscription + pricing ───
  const fetchAll = useCallback(async () => {
    try {
      const [subRes, pricingRes, renewRes, summaryRes, trialEndRes] = await Promise.all([
        fetch(`${API}/api/billing/subscription`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/superadmin/public-pricing-v2`),
        fetch(`${API}/api/payments/subscription-status`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/billing/pricing-summary`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/billing/trial-end-info`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (subRes.ok) setSubData(await subRes.json());
      if (pricingRes.ok) setPricing(await pricingRes.json());
      if (renewRes.ok) setRenewalInfo(await renewRes.json());
      if (summaryRes.ok) setPricingSummary(await summaryRes.json());
      if (trialEndRes.ok) setTrialEndInfo(await trialEndRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const checkUpgradeStatus = useCallback(async (stored) => {
    if (!stored?.request_id) return;
    try {
      const response = await fetch(`${API}/api/billing/upgrade-status/${stored.request_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 404) {
        sessionStorage.removeItem(upgradeStorageKey);
        setUpgradePending(null);
        return;
      }
      if (!response.ok) return;
      const status = await response.json();
      if (["completed", "failed", "quoted"].includes(status.status)) {
        sessionStorage.removeItem(upgradeStorageKey);
        setUpgradePending(null);
        if (status.status === "completed") {
          toast.success("Pagamento confermato: abbonamento aggiornato");
          fetchAll();
        }
      } else {
        setUpgradePending(stored);
      }
    } catch {
      setUpgradePending(stored);
    }
  }, [token, upgradeStorageKey, fetchAll]);

  useEffect(() => {
    if (!user?.societa_id) return;
    try {
      const stored = JSON.parse(sessionStorage.getItem(upgradeStorageKey) || "null");
      if (stored) checkUpgradeStatus(stored);
    } catch {
      sessionStorage.removeItem(upgradeStorageKey);
    }
  }, [user?.societa_id, upgradeStorageKey, checkUpgradeStatus]);

  // ─── Phase C: Trial-end choice handler ───
  const submitTrialEndChoice = async (choice) => {
    setTrialChoiceLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/trial-end-choice`, {
        method: "POST",
        headers,
        body: JSON.stringify({ choice }),
      });
      const json = await res.json();
      if (res.ok) {
        toast.success(json.message || "Scelta confermata");
        await fetchAll();
      } else {
        toast.error(json.detail || "Errore nel salvataggio della scelta");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setTrialChoiceLoading(false);
    }
  };

  // ─── Pre-seleziona pacchetto da ?feature= (tooltip upsell del menu) ───
  useEffect(() => {
    const featureParam = searchParams.get("feature");
    if (!featureParam || !pricing || !pricingSummary) return;

    // Mappa chiavi frontend → chiavi backend (stesso FEATURE_ALIASES di FeatureContext)
    const FEATURE_TO_BACKEND = {
      prodotti_catalogo:  ["food_beverage", "materie_prime", "acquisti_fornitori"],
      ordini_fornitori:   ["food_beverage", "attrezzature_packaging", "acquisti_fornitori"],
      fatture_acquisto:   ["storico_fatture", "food_beverage", "acquisti_fatture", "acquisti_fornitori"],
      fornitori:          ["fornitori", "acquisti_fornitori", "food_beverage"],
      menu_vendita:       ["menu_vendita", "food_beverage"],
      ricette:            ["ricette", "food_beverage"],
      presenze:           ["presenze"],
      turni:              ["turni", "presenze"],
      buste_paga:         ["buste_paga", "paghe"],
      bot_telegram:       ["bot_telegram"],
      controllo_temperature: ["controllo_temperature"],
      tracciabilita_lotti:   ["tracciabilita_lotti"],
      piano_pulizie:         ["piano_pulizie"],
      segnalazioni:          ["segnalazioni"],
      bacheca_compiti:       ["bacheca_compiti"],
      archivio_documenti:    ["archivio_documenti"],
    };

    const targetKeys = FEATURE_TO_BACKEND[featureParam] || [featureParam];

    const match = pricing.pacchetti.find(pkg => {
      const features = pkg.features || [];
      return targetKeys.some(k => features.includes(k));
    });

    if (!match) return;

    const owned = new Set(
      (pricingSummary?.pacchetti_principali || [])
        .filter(p => p.owned)
        .map(p => p.id)
    );
    if (owned.has(match.id)) return; // già posseduto, nessun pre-select

    setSelectedPkgs(prev => new Set([...prev, match.id]));
    setHighlightedPkgId(match.id);

    // Scroll alla card evidenziata
    setTimeout(() => {
      const el = document.getElementById(`pkg-card-${match.id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);

    // Rimuovi highlight dopo 3 secondi
    setTimeout(() => setHighlightedPkgId(null), 3400);
  }, [pricing, pricingSummary, searchParams]);
  useEffect(() => {
    if (!pricing || (selectedPkgs.size === 0 && addonTelegramQty === 0 && addonLocaleExtra === 0)) { setCart(null); return; }
    const calcCart = async () => {
      setCartLoading(true);
      try {
        const params = new URLSearchParams({
          package_ids: [...selectedPkgs].join(","),
          addon_telegram_qty: addonTelegramQty,
          addon_locale_extra: addonLocaleExtra,
          codice_sconto: upgradeCodice.trim(),
        });
        const res = await fetch(`${API}/api/billing/proration-preview?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setCart(await res.json());
      } catch { }
      setCartLoading(false);
    };
    calcCart();
  }, [selectedPkgs, addonTelegramQty, addonLocaleExtra, upgradeCodice, pricing]);

  // ─── Stripe return: poll ───
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const provider = searchParams.get("provider");
    const cancelled = searchParams.get("cancelled");
    const setupSuccess = searchParams.get("setup_success");
    const setupSessionId = searchParams.get("setup_session_id");
    const setupCancelled = searchParams.get("setup_cancelled");

    if (cancelled || setupCancelled) {
      toast.error(setupCancelled ? "Modifica carta annullata" : "Pagamento annullato");
      navigate("/abbonamento", { replace: true });
      return;
    }

    if (setupSuccess && setupSessionId) {
      // Confirm setup (set as default payment method on Stripe) - ASPETTA il risultato
      (async () => {
        try {
          const r = await fetch(`${API}/api/billing/setup-payment-method/confirm?setup_session_id=${setupSessionId}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          });
          const d = await r.json().catch(() => ({}));
          if (d.success) {
            toast.success("Carta aggiornata con successo!");
          } else {
            toast.error(`Impossibile salvare la carta: ${d.reason || "errore sconosciuto"}`);
          }
        } catch {
          toast.error("Errore di connessione durante il salvataggio carta");
        } finally {
          navigate("/abbonamento", { replace: true });
          // Ricarica tutti i dati (incluso payment-method via refreshCounter)
          setRefreshCounter(c => c + 1);
          fetchAll();
        }
      })();
      return;
    }

    if (sessionId && provider === "stripe") {
      setPollingStatus("Verifica pagamento in corso...");
      let attempts = 0;
      const poll = async () => {
        if (attempts >= 10) {
          setPollingStatus(null);
          toast.error("Timeout verifica. Riprova tra qualche minuto.");
          navigate("/abbonamento", { replace: true });
          return;
        }
        try {
          const res = await fetch(`${API}/api/payments/stripe/status/${sessionId}`, { headers });
          if (res.ok) {
            const d = await res.json();
            if (d.payment_status === "paid") {
              setPollingStatus(null);
              toast.success("Pagamento completato! Abbonamento attivato.");
              fetchAll();
              // Naviga alla dashboard con wizard_check per attivare eventuali wizard
              navigate("/?wizard_check=1", { replace: true });
              return;
            }
            if (d.status === "expired") {
              setPollingStatus(null);
              toast.error("Sessione scaduta. Riprova.");
              navigate("/abbonamento", { replace: true });
              return;
            }
          }
        } catch { }
        attempts++;
        setTimeout(poll, 2500);
      };
      poll();
    }
  }, [searchParams]);

  // ─── Toggle package ───
  const togglePkg = (pkgId) => {
    setSelectedPkgs(prev => {
      const n = new Set(prev);
      n.has(pkgId) ? n.delete(pkgId) : n.add(pkgId);
      return n;
    });
  };

  // ─── Cancel subscription ───
  const CANCEL_REASONS = [
    { key: "costo", label: "Il costo è troppo alto" },
    { key: "non_uso", label: "Non uso abbastanza l'app" },
    { key: "feature_mancanti", label: "Mancano funzionalità che mi servono" },
    { key: "bug_tecnici", label: "Problemi tecnici / bug" },
    { key: "altro_gestionale", label: "Sto passando ad un altro gestionale" },
    { key: "chiusura", label: "Ho chiuso o cambiato attività" },
    { key: "altro", label: "Altro" },
  ];

  const openCancelModal = async () => {
    setCancelOpen(true);
    setCancelPreview(null);
    setCancelReasonKey("");
    setCancelReasonNote("");
    setCancelMode("fine_contratto");
    try {
      const res = await fetch(`${API}/api/billing/cancel-preview`, { headers });
      if (res.ok) setCancelPreview(await res.json());
    } catch { toast.error("Errore caricamento dati disdetta"); }
  };

  const confirmCancel = async () => {
    if (!cancelReasonKey) {
      toast.error("Indica il motivo della disdetta");
      return;
    }
    const reasonLabel = CANCEL_REASONS.find(r => r.key === cancelReasonKey)?.label || cancelReasonKey;
    const reason = cancelReasonNote ? `${reasonLabel} — ${cancelReasonNote}` : reasonLabel;
    const mode = cancelPreview?.in_trial ? "subito" : cancelMode;
    setCancelLoading(true);
    try {
      const res = await fetch(`${API}/api/billing/cancel`, {
        method: "POST",
        headers,
        body: JSON.stringify({ confirm: true, reason, mode }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.message || "Disdetta registrata");
        setCancelOpen(false);
        await fetchAll();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore disdetta");
      }
    } catch { toast.error("Errore di connessione"); }
    setCancelLoading(false);
  };

  // ─── Checkout ───
  const handleCheckout = async (confirmUpgrade = false) => {
    if (selectedPkgs.size === 0 && addonTelegramQty === 0 && addonLocaleExtra === 0) { toast.error("Seleziona almeno un pacchetto o addon"); return; }
    if (upgradePending) { toast.error("Verifica l'upgrade in corso prima di effettuare altri acquisti"); return; }
    setCheckoutLoading(true);
    let existingSubscription = false;
    let savedRequest = null;
    try {
      existingSubscription = Boolean(cart?.has_subscription);
      if (existingSubscription) {
        const fingerprint = JSON.stringify([Array.from(selectedPkgs).sort(), addonTelegramQty, addonLocaleExtra, upgradeCodice.trim().toUpperCase()]);
        if (confirmUpgrade && (!upgradeQuote || upgradeQuote.fingerprint !== fingerprint)) {
          toast.error("La selezione è cambiata: richiedi un nuovo preventivo");
          return;
        }
        const old = JSON.parse(sessionStorage.getItem(upgradeStorageKey) || "null");
        if (old && old.fingerprint !== fingerprint) {
          setUpgradePending(old);
          toast.error("Un upgrade precedente è ancora in verifica");
          return;
        }
        savedRequest = old || { request_id: crypto.randomUUID(), fingerprint };
        sessionStorage.setItem(upgradeStorageKey, JSON.stringify(savedRequest));
      }
      const res = await fetch(`${API}/api/billing/checkout-package`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          package_ids: [...selectedPkgs],
          addon_telegram_qty: addonTelegramQty,
          addon_locale_extra: addonLocaleExtra,
          codice_sconto: upgradeCodice.trim(),
          ...(existingSubscription ? { upgrade_request_id: savedRequest.request_id } : {}),
          ...(existingSubscription ? { preview_only: !confirmUpgrade } : {}),
          success_url: window.location.href,
          cancel_url: window.location.href,
        }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.mode === "stripe_upgrade_quote") {
          setUpgradeQuote({ ...d, fingerprint: savedRequest.fingerprint });
        } else if (d.checkout_url) {
          // Reindirizza alla pagina Stripe Checkout (nuovo abbonamento)
          window.location.href = d.checkout_url;
        } else if (d.url) {
          window.location.href = d.url;
        } else if (d.mode === "stripe_upgrade" && d.updated) {
          setUpgradeQuote(null);
          sessionStorage.removeItem(upgradeStorageKey);
          setUpgradePending(null);
          const nomi = (d.package_names || []).join(", ");
          toast.success(`Upgrade confermato! ${nomi ? `(${nomi})` : ""}`);
          setTimeout(() => window.location.reload(), 1500);
        } else if (d.mode === "stripe_upgrade_pending") {
          setUpgradeQuote(null);
          setUpgradePending(savedRequest);
          toast.info(d.message || "Pagamento in verifica: nessun servizio attivato");
        } else if (d.mode === "mock" && d.activated) {
          // Mock mode: pacchetti attivati direttamente nel DB
          const nomi = (d.package_names || []).join(", ");
          toast.success(`Attivato! ${nomi ? `(${nomi})` : ""}`);
          setTimeout(() => window.location.reload(), 1500);
        } else {
          toast.error("Errore: risposta imprevista dal server");
        }
      } else {
        const err = await res.json().catch(() => ({}));
        if (res.status === 409 && /preventivo|importo cambiato/i.test(err.detail || "")) {
          setUpgradeQuote(null);
          toast.error(err.detail);
          return;
        }
        if (existingSubscription && [400, 402, 404, 422].includes(res.status)) {
          sessionStorage.removeItem(upgradeStorageKey);
        } else if (existingSubscription) {
          setUpgradePending(savedRequest);
        }
        toast.error(err.detail || "Errore checkout");
      }
    } catch {
      if (existingSubscription) setUpgradePending(savedRequest);
      toast.error(existingSubscription ? "Pagamento in verifica: controlla lo stato prima di riprovare" : "Errore di connessione");
    } finally {
      setCheckoutLoading(false);
    }
  };

  // ─── Check owned packages ───
  const subscription = subData?.subscription || subData;
  // Use package_ids from billing endpoint (most reliable), fallback to resolved packages array
  const ownedPkgIds = new Set(
    (subscription?.package_ids?.length > 0)
      ? subscription.package_ids
      : (subscription?.packages || []).map(p => p.id)
  );
  const isActive = subscription?.attivo === true;
  const hasSubscription = !!subscription?.id;
  // Compute expiry from subscription data or renewalInfo
  const scadenzaDisplay = renewalInfo?.scadenza_display || (subscription?.data_scadenza
    ? (() => { try { return new Date(subscription.data_scadenza).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; } })()
    : "");
  const daysLeft = renewalInfo?.days_left ?? null;
  const statusBadge = renewalInfo?.status_badge || (isActive ? "attivo" : "scaduto");
  const isTrial = renewalInfo?.is_trial === true || user?.subscription?.trial_attivo === true;

  // Date contratto (12 mesi) + prossima fattura mensile (dal backend)
  const contrattoFineDisplay = renewalInfo?.contratto_fine_display || scadenzaDisplay;
  const contrattoGiorni = renewalInfo?.contratto_giorni_rimanenti ?? daysLeft;
  const prossimoRinnovoDisplay = renewalInfo?.prossimo_rinnovo_display || "";
  const prossimoRinnovoGiorni = renewalInfo?.prossimo_rinnovo_giorni ?? null;

  // Prossimo rinnovo MENSILE — usa il giorno di data_scadenza (es. 20/04/2027 → billing day = 20)
  // Fallback a data_inizio se data_scadenza non disponibile
  const nextMonthlyBillingDisplay = (() => {
    const anchorStr = subscription?.data_scadenza || subscription?.data_inizio || subscription?.created_at;
    if (!anchorStr) return scadenzaDisplay;
    try {
      const anchor = new Date(anchorStr);
      const billingDay = anchor.getDate();
      const safe = Math.min(billingDay, 28);
      const now = new Date();
      let candidate = new Date(now.getFullYear(), now.getMonth(), safe, 0, 0, 0);
      if (candidate <= now) {
        candidate = new Date(now.getFullYear(), now.getMonth() + 1, safe, 0, 0, 0);
      }
      return candidate.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
    } catch { return scadenzaDisplay; }
  })();

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );

  if (pollingStatus) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
      <p className="text-lg font-semibold text-slate-700">{pollingStatus}</p>
      <p className="text-sm text-slate-400">Non chiudere questa finestra...</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-8" data-testid="abbonamento-page">

      {/* ═══ PHASE C: Modal forzato fine trial ═══ */}
      {trialEndInfo?.trial_end_choice_required && !trialModalDismissed && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          data-testid="trial-end-choice-modal"
        >
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div
              className="p-6 md:p-8 rounded-t-3xl relative"
              style={{ background: "linear-gradient(135deg, #6C63FF, #EC4899)" }}
            >
              {/* Bottone chiudi */}
              <button
                onClick={() => setTrialModalDismissed(true)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                aria-label="Chiudi"
                data-testid="trial-modal-close"
              >
                <X className="h-4 w-4 text-white" />
              </button>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div className="text-white">
                  <p className="text-xs font-bold uppercase tracking-wider opacity-80 mb-1">
                    {trialEndInfo.days_left <= 0 ? "Prova terminata" : `Ultimi ${trialEndInfo.days_left} giorn${trialEndInfo.days_left === 1 ? "o" : "i"} di prova`}
                  </p>
                  <h2 className="text-2xl md:text-3xl font-black leading-tight">
                    Scegli come continuare con Yollo
                  </h2>
                  <p className="text-sm text-white/90 mt-2 leading-relaxed">
                    La tua prova sta per terminare. Scegli la configurazione che vuoi mantenere — verrà addebitata mensilmente.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 md:p-8 space-y-3">
              {/* OPZIONE 1: FULL */}
              <button
                onClick={() => submitTrialEndChoice("full")}
                disabled={trialChoiceLoading}
                className="w-full text-left rounded-2xl border-2 border-indigo-200 hover:border-indigo-500 hover:shadow-lg p-5 transition-all bg-gradient-to-br from-indigo-50/60 to-purple-50/40 disabled:opacity-50"
                data-testid="trial-choice-full"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Crown className="h-4 w-4 text-indigo-600" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">Consigliato</span>
                    </div>
                    <h3 className="text-lg font-black text-slate-900 mb-1">Suite completa</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Mantieni l'accesso a <strong>tutti i pacchetti</strong> ({(trialEndInfo.full_config?.packages || []).map(p => p.nome).join(", ")}). Massimo risparmio con la combo.
                    </p>
                    {trialEndInfo.full_config?.saving_vs_singoli > 0 && (
                      <p className="text-xs font-bold text-emerald-600 mt-2">
                        Risparmi {trialEndInfo.full_config.saving_vs_singoli.toFixed(2)}€/mese rispetto ai singoli
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl font-black text-slate-900">{trialEndInfo.full_config?.monthly_amount?.toFixed(2)}€</div>
                    <p className="text-xs text-slate-400">/mese + IVA</p>
                  </div>
                </div>
              </button>

              {/* OPZIONE 2: INITIAL */}
              <button
                onClick={() => submitTrialEndChoice("initial")}
                disabled={trialChoiceLoading}
                className="w-full text-left rounded-2xl border-2 border-slate-200 hover:border-slate-400 hover:shadow-md p-5 transition-all bg-white disabled:opacity-50"
                data-testid="trial-choice-initial"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Check className="h-4 w-4 text-slate-500" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Configurazione iniziale</span>
                    </div>
                    <h3 className="text-lg font-black text-slate-900 mb-1">Mantieni quanto scelto</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Continua con i pacchetti scelti in registrazione: <strong>{(trialEndInfo.initial_config?.packages || []).map(p => p.nome).join(", ") || "configurazione minima"}</strong>.
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl font-black text-slate-900">{trialEndInfo.initial_config?.monthly_amount?.toFixed(2)}€</div>
                    <p className="text-xs text-slate-400">/mese + IVA</p>
                  </div>
                </div>
              </button>

              {/* OPZIONE 3: CANCEL */}
              <button
                onClick={() => submitTrialEndChoice("cancel")}
                disabled={trialChoiceLoading}
                className="w-full text-left rounded-2xl border-2 border-slate-100 hover:border-red-300 p-5 transition-all bg-slate-50/50 disabled:opacity-50"
                data-testid="trial-choice-cancel"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <X className="h-4 w-4 text-slate-400" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Disdetta</span>
                    </div>
                    <h3 className="text-base font-bold text-slate-700 mb-1">Annulla — non voglio continuare</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Nessun addebito. L'accesso a Yollo verrà sospeso al termine della prova.
                    </p>
                  </div>
                </div>
              </button>

              <p className="text-[11px] text-slate-400 text-center pt-2 leading-relaxed">
                Se non scegli entro la fine del trial, manterremo automaticamente la <strong>configurazione iniziale</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ═══ HEADER ═══ */}
      <div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900">Il Tuo Abbonamento</h1>
        <p className="text-base text-slate-500 mt-1">Scegli i pacchetti che servono al tuo locale, paga mensilmente.</p>
      </div>

      {/* ═══ ABBONAMENTO ATTIVO — STATUS + RECEIPT ═══ */}
      {hasSubscription && (
        <div className="space-y-3" data-testid="abbonamento-attivo-section">

          {/* ── Status Header ── */}
          <div className={`rounded-2xl border p-5 shadow-sm ${isActive ? "bg-white border-stone-200" : "bg-amber-50 border-amber-200"}`} data-testid="sub-status-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isTrial ? "bg-indigo-50" : isActive ? "bg-emerald-50" : "bg-amber-100"
                }`}>
                  {isTrial ? <Sparkles className="h-5 w-5 text-indigo-600" /> : isActive ? <Crown className="h-5 w-5 text-emerald-700" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
                </div>
                <div>
                  <p className={`text-sm font-bold tracking-widest ${
                    isTrial ? "text-indigo-600" : isActive ? "text-emerald-700" : "text-amber-700"
                  }`}>
                    {isTrial ? "PERIODO DI PROVA" : isActive ? "ABBONAMENTO ATTIVO" : "ABBONAMENTO SCADUTO"}
                  </p>
                  {scadenzaDisplay && (
                    <p className="text-xs text-stone-500 mt-0.5">
                      {isTrial
                        ? <>Periodo di prova · Termina il <span className="font-semibold text-stone-700">{scadenzaDisplay}</span></>
                        : <>Contratto annuale · {isActive ? "Scade" : "Scaduto"} il <span className="font-semibold text-stone-700">{contrattoFineDisplay}</span></>
                      }
                    </p>
                  )}
                  {!isTrial && isActive && prossimoRinnovoDisplay && (
                    <p className="text-[11px] text-stone-400 mt-0.5" data-testid="next-billing-line">
                      Prossima fattura mensile: <span className="font-semibold text-stone-600">{prossimoRinnovoDisplay}</span>
                      {prossimoRinnovoGiorni != null && <> · tra {prossimoRinnovoGiorni} giorn{prossimoRinnovoGiorni === 1 ? "o" : "i"}</>}
                    </p>
                  )}
                </div>
              </div>
              {daysLeft != null && isActive && (
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  {isTrial ? (
                    <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-indigo-50 text-indigo-600" data-testid="days-left-badge">
                      {daysLeft} giorni di prova rimanenti
                    </span>
                  ) : (
                    <>
                      {prossimoRinnovoGiorni != null && (
                        <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-sky-50 text-sky-700" data-testid="next-billing-badge">
                          Rinnovo mensile tra {prossimoRinnovoGiorni} giorn{prossimoRinnovoGiorni === 1 ? "o" : "i"}
                        </span>
                      )}
                      {contrattoGiorni != null && (
                        <span className={`px-3 py-1.5 rounded-xl text-xs font-bold ${
                          contrattoGiorni < 0 ? "bg-red-50 text-red-600" :
                          contrattoGiorni <= 30 ? "bg-amber-50 text-amber-600" :
                          "bg-emerald-50 text-emerald-600"
                        }`} data-testid="days-left-badge">
                          Contratto: {contrattoGiorni > 0 ? `${contrattoGiorni} giorni alla scadenza` : "scaduto"}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Disdetta programmata */}
            {isActive && subscription?.cancel_at_period_end && (
              <div className="mt-4 p-3 bg-amber-50 rounded-xl border border-amber-200" data-testid="cancel-pending-banner">
                <p className="text-xs font-semibold text-amber-800">
                  Disdetta programmata. L'abbonamento resterà attivo fino al {scadenzaDisplay || "termine del periodo"}.
                </p>
              </div>
            )}
            {isTrial && (
              <div className="mt-4 p-3 bg-indigo-50 rounded-xl border border-indigo-100" data-testid="trial-info-banner">
                <p className="text-xs text-indigo-700">
                  Stai usufruendo del periodo di prova gratuito. Al termine potrai scegliere un piano a pagamento per continuare ad usare Yollo.
                </p>
              </div>
            )}
            {!isActive && (
              <div className="mt-3 p-3 bg-amber-100 rounded-xl border border-amber-200">
                <p className="text-xs font-semibold text-amber-800">Il tuo abbonamento è scaduto. Seleziona i pacchetti qui sotto per rinnovarlo.</p>
              </div>
            )}
          </div>

          {/* ── Receipt-style cost breakdown ── */}
          {isActive && !subscription?.gratuito && pricingSummary && pricingSummary.totale_mensile > 0 && (
            <div className="bg-stone-50 rounded-2xl border border-stone-200 p-5" data-testid="cost-receipt">
              <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-4">Riepilogo abbonamento mensile</p>

              {/* Pacchetti base */}
              {(pricingSummary.pacchetti_principali || []).filter(p => p.owned).map(p => (
                <div key={p.id} className="flex items-center justify-between py-2.5 border-b border-dashed border-stone-200">
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-stone-800">{p.nome}</span>
                  </div>
                  <span className="text-sm font-mono font-semibold text-stone-900 tabular-nums">
                    {(p.prezzo_mensile ?? p.prezzo_mensile_annuale ?? 0).toFixed(2)}€<span className="text-xs text-stone-400 font-normal">/mese</span>
                  </span>
                </div>
              ))}

              {/* Locali extra */}
              {pricingSummary.locali?.extra > 0 && (
                <div className="flex items-center justify-between py-2.5 border-b border-dashed border-stone-200">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-stone-800">
                      {pricingSummary.locali.extra} {pricingSummary.locali.extra === 1 ? "Locale extra" : "Locali extra"}
                    </span>
                  </div>
                  <span className="text-sm font-mono font-semibold text-stone-900 tabular-nums">
                    {pricingSummary.locali.costo_mensile.toFixed(2)}€<span className="text-xs text-stone-400 font-normal">/mese</span>
                  </span>
                </div>
              )}

              {/* Bot Telegram — pagato (attivo o in configurazione) */}
              {pricingSummary.telegram_bot?.attivo && !pricingSummary.telegram_bot?.pending && (
                <div className="py-2.5 border-b border-dashed border-stone-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bot className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-stone-800">
                        Bot Telegram{pricingSummary.telegram_bot.numeri_acquistati > 1 ? ` (${pricingSummary.telegram_bot.numeri_acquistati} numeri)` : ""}
                      </span>
                    </div>
                    <span className="text-sm font-mono font-semibold text-stone-900 tabular-nums">
                      {pricingSummary.telegram_bot.costo_mensile.toFixed(2)}€<span className="text-xs text-stone-400 font-normal">/mese</span>
                    </span>
                  </div>
                  {pricingSummary.telegram_bot.in_configurazione && (
                    <div className="flex items-center gap-1.5 mt-1.5 ml-5">
                      <Loader2 className="h-3 w-3 text-amber-500 animate-spin" />
                      <span className="text-[11px] text-amber-700 font-medium">In configurazione — operativo entro 48h</span>
                    </div>
                  )}
                </div>
              )}

              {/* Bot Telegram — vecchio flusso non pagato */}
              {pricingSummary.telegram_bot?.pending && (
                <div className="flex items-center justify-between py-2.5 border-b border-dashed border-stone-200">
                  <div className="flex items-center gap-2">
                    <Bot className="h-3.5 w-3.5 text-stone-400 flex-shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-stone-500">Bot Telegram</span>
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">IN ATTESA</span>
                    </div>
                  </div>
                  <span className="text-xs text-stone-400 font-mono">non addebitato</span>
                </div>
              )}

              {/* Separatore e totale */}
              <div className="flex items-end justify-between pt-4 mt-1" data-testid="receipt-total">
                <span className="text-base font-bold text-stone-900">Totale mensile</span>
                <div className="text-right">
                  {pricingSummary.sconto && (
                    <span className="text-sm line-through text-stone-400 font-mono mr-2">{(pricingSummary.totale_mensile_pieno || 0).toFixed(2)}€</span>
                  )}
                  <span className="text-2xl font-black font-mono text-stone-900 tabular-nums">{pricingSummary.totale_mensile.toFixed(2)}€</span>
                  <span className="text-sm text-stone-500 ml-1.5">+ IVA</span>
                </div>
              </div>
              {pricingSummary.sconto && (
                <div className="flex items-center justify-end mt-1" data-testid="receipt-sconto">
                  <span className="text-xs font-bold text-emerald-600">
                    Sconto {pricingSummary.sconto.codice} — {pricingSummary.sconto.tipo_sconto === "percentuale" ? `-${pricingSummary.sconto.valore}%` : `-${pricingSummary.sconto.valore}€`} · {pricingSummary.sconto.durata_sconto === "primo_mese" ? "primo mese" : "applicato ogni mese"}
                  </span>
                </div>
              )}
              {pricingSummary.sconto_primo_mese && (
                <div className="flex items-center justify-end mt-1" data-testid="receipt-sconto-primo-mese">
                  <span className="text-xs font-bold text-emerald-600">
                    Codice {pricingSummary.sconto_primo_mese.codice}: 1° mese {pricingSummary.sconto_primo_mese.primo_mese_totale.toFixed(2)}€ ({pricingSummary.sconto_primo_mese.tipo_sconto === "percentuale" ? `-${pricingSummary.sconto_primo_mese.valore}%` : `-${pricingSummary.sconto_primo_mese.valore}€`}), poi {pricingSummary.totale_mensile.toFixed(2)}€/mese
                  </span>
                </div>
              )}

              {/* Prossimo addebito */}
              {nextMonthlyBillingDisplay && (
                <div className="mt-4 flex items-center justify-between px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-100" data-testid="prossimo-addebito">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                    <div>
                      <span className="text-xs font-semibold text-emerald-700">Prossimo addebito</span>
                      <span className="ml-2 text-xs text-emerald-600">{nextMonthlyBillingDisplay}</span>
                    </div>
                  </div>
                  <span className="text-sm font-black font-mono text-emerald-800 tabular-nums">{pricingSummary.totale_mensile.toFixed(2)}€ <span className="text-xs font-normal">+ IVA</span></span>
                </div>
              )}
            </div>
          )}

          {/* Disdici abbonamento */}
          {isActive && !subscription?.cancel_at_period_end && (
            <div className="flex justify-end">
              <button
                onClick={openCancelModal}
                className="text-xs font-semibold text-stone-400 hover:text-red-500 transition-colors underline-offset-2 hover:underline"
                data-testid="open-cancel-btn"
              >
                Disdici abbonamento
              </button>
            </div>
          )}
        </div>
      )}

      {/* ═══ GRUPPO COMPATTO: Fatturazione + Carta + Storico ═══ */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <BillingInfoSection token={token} />
        <div className="h-px bg-slate-100" />
        <PaymentMethodSection token={token} refreshTrigger={refreshCounter} />
        {isActive && <>
          <div className="h-px bg-slate-100" />
          <StoricoSection token={token} />
        </>}
      </div>

      {/* ═══ I TUOI PACCHETTI ATTIVI — riepilogo cose acquistate con prezzi ═══ */}
      {pricingSummary && (() => {
        const owned = (pricingSummary.pacchetti_principali || []).filter(p => p.owned);
        const tg = pricingSummary.telegram_bot || {};
        const loc = pricingSummary.locale_aggiuntivo || {};
        const hasOwned = owned.length > 0 || tg.attivo || (loc.locali_extra > 0);
        if (!hasOwned) return null;
        return (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="owned-packages-section">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                  <Package className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">I tuoi pacchetti attivi</h2>
                  <p className="text-xs text-slate-400">Configurazione scelta in fase di registrazione</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Totale mensile</p>
                <p className="text-xl font-black text-slate-900">{(pricingSummary.totale_mensile || 0).toFixed(2)} €<span className="text-xs text-slate-400 font-medium ml-1">+IVA</span></p>
              </div>
            </div>
            <div className="divide-y divide-slate-50">
              {owned.map(pkg => (
                <div key={pkg.id} className="flex items-center justify-between px-5 py-3.5" data-testid={`owned-pkg-${pkg.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">{pkg.nome}</p>
                    {pkg.descrizione && <p className="text-xs text-slate-400 truncate">{pkg.descrizione}</p>}
                  </div>
                  <p className="text-sm font-bold text-slate-700 ml-3">{(pkg.prezzo_mensile || 0).toFixed(2)} €<span className="text-xs text-slate-400 font-normal">/mese</span></p>
                </div>
              ))}
              {tg.attivo && (
                <div className="flex items-center justify-between px-5 py-3.5" data-testid="owned-telegram">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800">Bot Telegram</p>
                    <p className="text-xs text-slate-400">{tg.numeri_acquistati || 0} numero{(tg.numeri_acquistati || 0) === 1 ? "" : "i"} attivi</p>
                  </div>
                  <p className="text-sm font-bold text-slate-700 ml-3">{(tg.costo_mensile || 0).toFixed(2)} €<span className="text-xs text-slate-400 font-normal">/mese</span></p>
                </div>
              )}
              {(loc.locali_extra || 0) > 0 && (
                <div className="flex items-center justify-between px-5 py-3.5" data-testid="owned-locali-extra">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800">Locali aggiuntivi</p>
                    <p className="text-xs text-slate-400">{loc.locali_extra} extra · {loc.totale} totali</p>
                  </div>
                  <p className="text-sm font-bold text-slate-700 ml-3">{(loc.costo_mensile || 0).toFixed(2)} €<span className="text-xs text-slate-400 font-normal">/mese</span></p>
                </div>
              )}
            </div>
            {isTrial && (
              <div className="px-5 py-3 bg-amber-50/60 border-t border-amber-100">
                <p className="text-xs text-amber-800">
                  ⓘ Durante il periodo di prova non paghi nulla. Al termine ti verranno addebitati <b>{(pricingSummary.totale_mensile || 0).toFixed(2)} €/mese + IVA</b> per questa configurazione.
                </p>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ PHASE B: durante trial, mostra messaggio "tutto attivo" invece di selezione pacchetti ═══ */}
      {isTrial && (
        <div
          className="relative overflow-hidden rounded-2xl border-2 p-6 md:p-8"
          style={{
            background: "linear-gradient(135deg, #F5F3FF 0%, #FDF2F8 50%, #FEF3C7 100%)",
            borderColor: "#C7D2FE",
          }}
          data-testid="trial-full-access-section"
        >
          {/* Decorative blob */}
          <div
            className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-30 pointer-events-none"
            style={{ background: "radial-gradient(circle, #A78BFA 0%, transparent 70%)" }}
          />
          <div className="relative z-10 flex items-start gap-4">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #6C63FF, #EC4899)" }}
            >
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl md:text-2xl font-black text-slate-900 mb-2">
                Hai accesso completo a tutta la piattaforma
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                Durante i giorni di prova puoi <strong>esplorare e usare gratis ogni pacchetto e add-on</strong>: HACCP, Acquisti, Personale, Bot Telegram, Locali extra, tutto incluso. Al termine della prova, ti chiederemo di scegliere la configurazione che preferisci tenere attiva — pagherai solo per quella.
              </p>
              {daysLeft != null && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-indigo-200 backdrop-blur-sm" data-testid="trial-days-pill">
                  <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
                  <span className="text-xs font-bold text-indigo-700">
                    {daysLeft <= 0 ? "Prova terminata" : `${daysLeft} giorn${daysLeft === 1 ? "o" : "i"} di prova rimanenti`}
                  </span>
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                <NavLink
                  to="/"
                  className="px-4 py-2 rounded-xl text-sm font-bold text-white shadow-sm hover:opacity-90 transition-opacity"
                  style={{ background: "linear-gradient(135deg, #6C63FF, #8B5CF6)" }}
                  data-testid="trial-explore-btn"
                >
                  Esplora la piattaforma →
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PACKAGES GRID — nascosta durante trial (Phase B) ═══ */}
      {!isTrial && pricing && (
        <div data-testid="packages-section">
          <div className="flex items-center gap-2 mb-4">
            <Package className="h-5 w-5 text-slate-400" />
            <h2 className="text-xl font-bold text-slate-900">Pacchetti disponibili</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {pricing.pacchetti.map(pkg => {
              const isSelected = selectedPkgs.has(pkg.id);
              const isOwned = ownedPkgIds.has(pkg.id);
              const hasAcquisti = pkg.nome.toLowerCase().includes(pricing.pacchetto_acquisti_keyword || "acquisti");

              return (
                <div
                  key={pkg.id}
                  id={`pkg-card-${pkg.id}`}
                  onClick={() => !isOwned && togglePkg(pkg.id)}
                  className={`relative rounded-[24px] border-2 overflow-hidden transition-all cursor-pointer select-none flex flex-col h-full
                    ${isOwned ? "border-emerald-200 bg-emerald-50/50 cursor-default opacity-70" :
                      highlightedPkgId === pkg.id ? "border-indigo-500 bg-indigo-50/40 shadow-2xl shadow-indigo-200 ring-4 ring-indigo-300 ring-offset-2 animate-pulse" :
                      isSelected ? "border-indigo-500 bg-indigo-50/40 shadow-xl shadow-indigo-100" :
                      "border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg"
                    }`}
                  data-testid={`pkg-card-${pkg.id}`}
                >
                  {/* Immagine pacchetto 16:9 */}
                  {pkg.immagine_url && (
                    <div className="relative w-full aspect-[16/9] overflow-hidden">
                      <img 
                        src={pkg.immagine_url.startsWith("http") ? pkg.immagine_url : `${API}/api/superadmin/files/${pkg.immagine_url}`}
                        alt={pkg.nome}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                      {/* Checkbox sopra immagine */}
                      <div className={`absolute top-3 right-3 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all
                        ${isOwned ? "border-emerald-400 bg-emerald-500" :
                          isSelected ? "border-white bg-indigo-500" : "border-white/60 bg-white/30"}`}>
                        {(isSelected || isOwned) && <Check className="h-4 w-4 text-white" />}
                      </div>
                    </div>
                  )}
                  
                  <div className={`flex flex-col flex-grow ${pkg.immagine_url ? 'p-5 pt-4' : 'p-6'}`}>
                    {/* Selection indicator - solo se non c'è immagine */}
                    {!pkg.immagine_url && (
                      <div className={`absolute top-4 right-4 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all
                        ${isOwned ? "border-emerald-400 bg-emerald-500" :
                          isSelected ? "border-indigo-500 bg-indigo-500" : "border-slate-300 bg-white"}`}>
                        {(isSelected || isOwned) && <Check className="h-4 w-4 text-white" />}
                      </div>
                    )}

                    <h3 className="text-lg font-black text-slate-900 pr-8 mb-1">{pkg.nome}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-4 min-h-[40px]">{pkg.descrizione}</p>

                    <div className="mb-4">
                      <span className="text-3xl font-black text-slate-900">{pkg.prezzo_mensile_annuale}</span>
                      <span className="text-base font-semibold text-slate-400">€/mese</span>
                      <span className="text-xs text-slate-400 ml-1">+ IVA</span>
                      {hasAcquisti && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">PREMIUM</span>}
                    </div>

                    {isOwned && (
                      <div className="mb-3 text-xs font-bold text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Gia incluso
                      </div>
                    )}

                    {pkg.feature_labels && pkg.feature_labels.length > 0 && (
                      <ul className="space-y-2 flex-grow">
                        {pkg.feature_labels.slice(0, 5).map((f, i) => (
                          <li key={i} className="text-xs text-slate-600 flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                              <Check className="h-2.5 w-2.5 text-indigo-500" />
                            </div>
                            {f}
                          </li>
                        ))}
                        {pkg.feature_labels.length > 5 && (
                          <li className="text-xs text-slate-400 pl-6">+{pkg.feature_labels.length - 5} altre funzionalita</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ═══ COMBO INFO ═══ */}
          {pricing.combo_rules.length > 0 && (
            <div className="mt-4 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-2xl p-5 border border-indigo-100" data-testid="combo-info">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                <h3 className="font-bold text-indigo-900 text-sm">Risparmia con le Combo</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {pricing.combo_rules.map((r, i) => (
                  <div key={i} className="bg-white/70 rounded-xl p-3 text-center backdrop-blur-sm">
                    <p className="text-xs font-semibold text-indigo-700">{r.label}</p>
                    <p className="text-xl font-black text-slate-900 mt-1">{r.prezzo_mensile}€<span className="text-xs font-medium text-slate-400">/mese + IVA</span></p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ ADDONS — nascosti durante trial (Phase B) ═══ */}
      {!isTrial && pricing && (
        <div data-testid="addons-section">
          <div className="flex items-center gap-2 mb-4">
            <Plus className="h-5 w-5 text-slate-400" />
            <h2 className="text-xl font-bold text-slate-900">Add-on</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

            {/* ── Bot Telegram ── */}
            {(() => {
              const ownedTg = pricingSummary?.telegram_bot?.numeri_acquistati || 0;
              const hasBot  = pricingSummary?.telegram_bot?.attivo && !pricingSummary?.telegram_bot?.pending;
              const priceFirst = pricing.addons?.telegram_bot?.prezzo_primo_numero ?? pricing.addons?.telegram_bot?.prezzo_mensile ?? 39.90;
              const priceExtra = pricing.addons?.telegram_bot?.prezzo_numero_extra ?? 9.90;
              const showPrice  = hasBot ? priceExtra : priceFirst;
              const priceTag   = hasBot ? `${showPrice.toFixed(2)}€ /cad. + IVA` : `${showPrice.toFixed(2)}€ /mese + IVA`;
              const isSelected = addonTelegramQty > 0;
              return (
                <div
                  className={`flex flex-col justify-between h-[148px] p-4 border-2 rounded-2xl transition-all ${
                    isSelected ? "border-blue-500 bg-blue-50/40" : "border-zinc-200 bg-white"
                  }`}
                  data-testid="addon-card-telegram"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <h4 className="font-bold text-sm text-zinc-900 truncate">Bot Telegram</h4>
                        <span className="text-xs font-bold text-zinc-900 whitespace-nowrap ml-1">{priceTag}</span>
                      </div>
                      <p className="text-xs text-zinc-500 truncate mt-0.5">Assistente AI per il locale</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-auto pt-2">
                    <div>
                      {hasBot && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 text-emerald-800 text-[11px] font-semibold whitespace-nowrap" data-testid="addon-status-badge-telegram">
                          <Check className="h-3 w-3" /> {ownedTg} {ownedTg === 1 ? "numero attivo" : "numeri attivi"}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl px-2 py-1 shadow-sm">
                      <button onClick={() => setAddonTelegramQty(Math.max(0, addonTelegramQty - 1))}
                        className="w-7 h-7 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-600 font-bold transition-colors"
                        data-testid="addon-stepper-decrement-telegram">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-5 text-center text-sm font-bold text-zinc-900" data-testid="addon-stepper-count-telegram">{addonTelegramQty}</span>
                      <button onClick={() => setAddonTelegramQty(addonTelegramQty + 1)}
                        className="w-7 h-7 rounded-lg hover:bg-blue-100 flex items-center justify-center text-blue-600 font-bold transition-colors"
                        data-testid="addon-stepper-increment-telegram">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Locale Extra ── */}
            {(() => {
              const ownedLocali = pricingSummary?.locali?.extra || 0;
              const priceLocale = pricing.addons?.locale_extra?.prezzo_extra_annuale ?? pricing.addons?.locale_extra?.prezzo_mensile ?? pricing.addons?.locale_aggiuntivo?.prezzo_mensile ?? 59;
              const isSelected  = addonLocaleExtra > 0;
              return (
                <div
                  className={`flex flex-col justify-between h-[148px] p-4 border-2 rounded-2xl transition-all ${
                    isSelected ? "border-amber-500 bg-amber-50/40" : "border-zinc-200 bg-white"
                  }`}
                  data-testid="addon-card-locale"
                  id="addon-locali"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <h4 className="font-bold text-sm text-zinc-900 truncate">Locale Extra</h4>
                        <span className="text-xs font-bold text-zinc-900 whitespace-nowrap ml-1">{priceLocale}€ /mese + IVA</span>
                      </div>
                      <p className="text-xs text-zinc-500 truncate mt-0.5">Gestisci più sedi</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-auto pt-2">
                    <div>
                      {ownedLocali > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 text-emerald-800 text-[11px] font-semibold whitespace-nowrap" data-testid="addon-status-badge-locale">
                          <Check className="h-3 w-3" /> {ownedLocali} {ownedLocali === 1 ? "locale extra attivo" : "locali extra attivi"}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl px-2 py-1 shadow-sm">
                      <button onClick={() => setAddonLocaleExtra(Math.max(0, addonLocaleExtra - 1))}
                        className="w-7 h-7 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-600 font-bold transition-colors"
                        data-testid="addon-stepper-decrement-locale">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-5 text-center text-sm font-bold text-zinc-900" data-testid="addon-stepper-count-locale">{addonLocaleExtra}</span>
                      <button onClick={() => setAddonLocaleExtra(addonLocaleExtra + 1)}
                        className="w-7 h-7 rounded-lg hover:bg-amber-100 flex items-center justify-center text-amber-600 font-bold transition-colors"
                        data-testid="addon-stepper-increment-locale">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ═══ CART SUMMARY — nascosto durante trial (Phase B) ═══ */}
      {!isTrial && (selectedPkgs.size > 0 || addonTelegramQty > 0 || addonLocaleExtra > 0) && (
        <div className="bg-white rounded-2xl border-2 border-slate-900 p-6 shadow-xl" data-testid="cart-summary">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingCart className="h-5 w-5 text-slate-900" />
            <h3 className="text-lg font-bold text-slate-900">Riepilogo</h3>
          </div>

          {cartLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : cart ? (
            <div className="space-y-3">
              {/* Pacchetti nuovi */}
              {cart.pacchetti_nuovi && cart.pacchetti_nuovi.map(p => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{p.nome}</span>
                  <span className={`text-xs ${cart.combo_applied ? "text-slate-400 line-through" : "text-slate-900 font-semibold"}`}>
                    {p.prezzo_singolo}€/mese + IVA
                  </span>
                </div>
              ))}

              {/* Combo applicata */}
              {cart.combo_applied && (
                <div className="flex items-center justify-between text-sm font-semibold bg-indigo-50 -mx-2 px-2 py-2 rounded-xl">
                  <span className="text-indigo-700 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> {cart.combo_applied}</span>
                  <span className="text-indigo-900">{cart.prezzo_mensile_nuovo?.toFixed(2)}€/mese</span>
                </div>
              )}
              {cart.combo_applied && cart.risparmio > 0 && (
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-emerald-50 border border-emerald-100">
                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="text-xs font-bold text-emerald-700">Combo attiva: risparmi {cart.risparmio.toFixed(2)}€/mese + IVA</span>
                </div>
              )}

              {/* Addon */}
              {(cart.addon_items || []).map((a, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{a.nome}</span>
                  <span className="text-slate-900">{a.prezzo.toFixed(2)}€/mese + IVA</span>
                </div>
              ))}

              {/* Codice sconto sull'aggiunta */}
              <div className="border-t border-slate-100 pt-3">
                <label className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1.5">
                  <Tag className="h-3.5 w-3.5" /> Codice sconto (opzionale)
                </label>
                <input
                  type="text"
                  value={upgradeCodice}
                  onChange={(e) => setUpgradeCodice(e.target.value.toUpperCase())}
                  placeholder="ES. NUOVOPKG10"
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono uppercase focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100"
                  data-testid="upgrade-discount-input"
                />
                {upgradeCodice.trim() && cart.sconto?.errore && (
                  <p className="text-xs text-red-500 mt-1.5" data-testid="upgrade-discount-error">{cart.sconto.errore}</p>
                )}
                {cart.sconto?.codice && (
                  <div className="flex items-center justify-between mt-2 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="upgrade-discount-applied">
                    <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                      <Check className="h-3.5 w-3.5" /> {cart.sconto.codice} · {cart.sconto.tipo_sconto === "percentuale" ? `-${cart.sconto.valore}%` : `-${cart.sconto.valore}€`}
                      {cart.sconto.durata_sconto === "primo_mese" ? " · solo 1° mese" : " · ricorrente"}
                    </span>
                    <span className="text-xs font-bold text-emerald-800">-{cart.sconto.sconto_oggi?.toFixed(2)}€ oggi</span>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 pt-3 mt-3" />

              {/* Proration: upgrade con abbonamento attivo */}
              {cart.has_subscription ? (
                <>
                  {/* Stima locale: il preventivo Stripe prima del pagamento è quello vincolante. */}
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1.5">
                    <p className="text-xs font-bold text-amber-800 uppercase tracking-wider">Stima oggi · importo confermato prima del pagamento</p>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-amber-700">
                        Quota proporzionale ({cart.giorni_rimanenti} giorni rimanenti su {cart.giorni_nel_ciclo})
                      </span>
                      <span className="text-lg font-black text-amber-900" data-testid="cart-today-amount">
                        {cart.importo_oggi_netto?.toFixed(2)}€ <span className="text-xs font-normal">+ IVA</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-amber-600 border-t border-amber-200 pt-1.5 mt-1">
                      <span>IVA ({cart.iva_percentuale}%)</span>
                      <span>{cart.iva_oggi?.toFixed(2)}€</span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-bold text-amber-900">
                      <span>Stima totale oggi</span>
                      <span data-testid="cart-total-today">{cart.totale_oggi_lordo?.toFixed(2)}€ IVA inclusa</span>
                    </div>
                  </div>

                  {/* DAL PROSSIMO RINNOVO */}
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1">
                    <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Dal {cart.data_prossimo_rinnovo_display || "prossimo rinnovo"}</p>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-slate-500">Canone mensile completo</span>
                      <span className="text-base font-black text-slate-900" data-testid="cart-total">
                        {cart.prezzo_mensile_futuro?.toFixed(2)}€/mese <span className="text-xs font-normal text-slate-400">+ IVA</span>
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400">
                      Totale lordo: {cart.totale_mensile_lordo?.toFixed(2)}€/mese (IVA {cart.iva_percentuale}% inclusa)
                    </p>
                  </div>
                </>
              ) : (
                /* Nuovo abbonamento: prezzo pieno */
                <div className="border-t-2 border-slate-900 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-black text-slate-900">Totale mensile</span>
                    <span className="text-2xl font-black text-slate-900" data-testid="cart-total">
                      {cart.prezzo_mensile_futuro?.toFixed(2)}€ <span className="text-sm font-normal text-slate-400">+ IVA</span>
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    IVA ({cart.iva_percentuale}%): {cart.iva_mensile?.toFixed(2)}€ — Totale lordo: {cart.totale_mensile_lordo?.toFixed(2)}€/mese
                  </p>
                </div>
              )}

              {upgradePending && (
                <div className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="upgrade-pending-alert">
                  <p data-testid="upgrade-pending-message">Un pagamento precedente è in verifica. Nessun nuovo acquisto sarà avviato.</p>
                  <button type="button" onClick={() => checkUpgradeStatus(upgradePending)}
                    className="mt-2 font-semibold underline" data-testid="upgrade-check-status-button">
                    Verifica stato
                  </button>
                </div>
              )}
              {/* Checkout button */}
              <Button
                onClick={() => handleCheckout(false)}
                disabled={checkoutLoading || Boolean(upgradePending) || (selectedPkgs.size === 0 && addonTelegramQty === 0 && addonLocaleExtra === 0)}
                className="w-full mt-4 h-14 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-base shadow-xl shadow-slate-200 transition-all"
                data-testid="checkout-btn"
              >
                {checkoutLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <CreditCard className="h-5 w-5 mr-2" />
                )}
                {checkoutLoading ? "Verifica in corso..." : (cart.has_subscription ? "Verifica importo su Stripe" : "Procedi al pagamento")}
              </Button>

              <div className="flex items-center justify-center gap-2 mt-2 text-xs text-slate-400">
                <Shield className="h-3.5 w-3.5" />
                <span>Pagamento sicuro via Stripe</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Seleziona i pacchetti per vedere il riepilogo</p>
          )}
        </div>
      )}
      <Dialog open={Boolean(upgradeQuote)} onOpenChange={(open) => {
        if (!open && !checkoutLoading) {
          setUpgradeQuote(null);
          sessionStorage.removeItem(upgradeStorageKey);
        }
      }}>
        <DialogContent data-testid="upgrade-quote-dialog" className="bg-white text-slate-900 w-[calc(100vw-2rem)] max-w-md rounded-md p-5 sm:p-6"
          style={{ animation: "none" }}
          onInteractOutside={(event) => event.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="pr-8 text-base leading-snug">Conferma modifica abbonamento</DialogTitle>
            <DialogDescription>Importo calcolato da Stripe per questa modifica, inclusi eventuali sconti e imposte.</DialogDescription>
          </DialogHeader>
          <p className="text-2xl font-bold break-words" data-testid="upgrade-quote-amount">
            {upgradeQuote?.first_payment_after_trial ? "Nessun addebito oggi" : `€${Number(upgradeQuote?.amount_today || 0).toFixed(2)} oggi`}
          </p>
          {upgradeQuote?.first_payment_after_trial && <p className="text-sm text-slate-600" data-testid="upgrade-trial-notice">Il primo pagamento avverrà alla fine della prova.</p>}
          {upgradeQuote?.discounted_items?.length > 0 && (
            <p className="text-sm text-emerald-700 [overflow-wrap:anywhere]" data-testid="upgrade-quote-discount-items">
              Sconto {upgradeQuote?.duration === "primo_mese" ? "sulla prima fattura pagata" : "ricorrente"} solo su: {upgradeQuote.discounted_items.join(", ")}
            </p>
          )}
          <div className="flex flex-wrap gap-2 justify-end mt-3">
            <Button variant="outline" data-testid="upgrade-quote-cancel" disabled={checkoutLoading}
              onClick={() => { setUpgradeQuote(null); sessionStorage.removeItem(upgradeStorageKey); }}>Annulla</Button>
            <Button data-testid="upgrade-quote-confirm" disabled={checkoutLoading}
              onClick={() => handleCheckout(true)}>
              {checkoutLoading ? "Verifica..." : "Conferma upgrade"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ EMPTY STATE — nascosto durante trial (Phase B) ═══ */}
      {!isTrial && !isActive && selectedPkgs.size === 0 && addonTelegramQty === 0 && addonLocaleExtra === 0 && (
        <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-200">
          <ShoppingCart className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">Seleziona uno o piu pacchetti per iniziare</p>
          <p className="text-sm text-slate-400 mt-1">Combina piu pacchetti per ottenere un prezzo scontato!</p>
        </div>
      )}

      {/* ═══ CANCEL MODAL ═══ */}
      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cancel-modal">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl flex flex-col" style={{ maxHeight: "90vh" }}>

            {/* Header fisso */}
            <div className="flex items-start justify-between px-6 pt-5 pb-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-black text-slate-900">Disdici abbonamento</h3>
              </div>
              <button onClick={() => setCancelOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Corpo scrollabile */}
            <div className="flex-1 overflow-y-auto px-6 pb-2">
            {!cancelPreview ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : !cancelPreview.can_cancel ? (
              <div>
                <p className="text-sm text-slate-600">{cancelPreview.reason || "Nessun abbonamento attivo da disdire."}</p>
              </div>
            ) : cancelPreview.in_trial ? (
              <div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                  <p className="text-sm font-bold text-emerald-800 mb-1">Sei in fase di prova gratuita</p>
                  <p className="text-xs text-emerald-700">
                    L'abbonamento sarà disdetto immediatamente <b>senza alcun addebito</b>.
                    I tuoi dati restano accessibili — la cancellazione fisica avviene solo su richiesta.
                  </p>
                </div>
                <div className="mb-2">
                  <p className="text-xs font-bold text-slate-700 mb-2">Aiutaci a capire: perché te ne vai? <span className="text-red-500">*</span></p>
                  <div className="space-y-1.5">
                    {CANCEL_REASONS.map(r => (
                      <label key={r.key}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs font-medium transition-all ${
                          cancelReasonKey === r.key
                            ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                        data-testid={`cancel-reason-${r.key}`}>
                        <input type="radio" name="cancel-reason" value={r.key}
                          checked={cancelReasonKey === r.key}
                          onChange={() => setCancelReasonKey(r.key)}
                          className="accent-indigo-600" />
                        {r.label}
                      </label>
                    ))}
                  </div>
                  {cancelReasonKey && (
                    <textarea
                      value={cancelReasonNote}
                      onChange={(e) => setCancelReasonNote(e.target.value.slice(0, 300))}
                      placeholder="Vuoi aggiungere qualcosa? (opzionale, max 300 caratteri)"
                      className="mt-2 w-full text-xs px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 resize-none"
                      rows={2}
                      data-testid="cancel-reason-note"
                    />
                  )}
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-600 mb-3">Scegli come vuoi disdire il tuo abbonamento:</p>

                {/* OPZIONE A: Disdici subito */}
                <button
                  type="button"
                  onClick={() => setCancelMode("subito")}
                  className={`w-full text-left rounded-2xl border-2 p-4 mb-3 transition-all ${
                    cancelMode === "subito" ? "border-red-400 bg-red-50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  data-testid="cancel-option-subito"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${cancelMode === "subito" ? "border-red-500" : "border-slate-300"}`}>
                      {cancelMode === "subito" && <div className="w-2.5 h-2.5 rounded-full bg-red-500" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-slate-900">Disdici subito e disattiva l'account</p>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                        {cancelPreview.in_contratto && cancelPreview.mesi_da_saldare > 0 ? (
                          <>Paghi ora l'intera rimanenza del contratto: <b>{cancelPreview.mesi_da_saldare} canoni</b> da {cancelPreview.importo_canone_mensile}€ = <b className="text-red-600">{cancelPreview.importo_residuo_subito}€</b> (IVA inclusa) in un'unica soluzione. L'account viene disattivato immediatamente.</>
                        ) : (
                          <>L'account viene disattivato immediatamente. Nessun importo residuo da saldare.</>
                        )}
                      </p>
                    </div>
                  </div>
                </button>

                {/* OPZIONE B: Disdici a fine contratto */}
                <button
                  type="button"
                  onClick={() => setCancelMode("fine_contratto")}
                  className={`w-full text-left rounded-2xl border-2 p-4 mb-4 transition-all ${
                    cancelMode === "fine_contratto" ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  data-testid="cancel-option-fine-contratto"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${cancelMode === "fine_contratto" ? "border-indigo-500" : "border-slate-300"}`}>
                      {cancelMode === "fine_contratto" && <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-slate-900">
                        Disdici a fine contratto{cancelPreview.data_fine_contratto ? ` (${cancelPreview.data_fine_contratto})` : ""}
                      </p>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                        Disattivi il <b>rinnovo automatico</b>. L'abbonamento resta attivo e paghi le rate mensili fino alla scadenza{cancelPreview.data_fine_contratto ? <> del <b>{cancelPreview.data_fine_contratto}</b></> : ""}, poi si chiude automaticamente.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Survey "perché te ne vai?" */}
                <div className="mb-2">
                  <p className="text-xs font-bold text-slate-700 mb-2">Aiutaci a capire: perché te ne vai? <span className="text-red-500">*</span></p>
                  <div className="space-y-1.5">
                    {CANCEL_REASONS.map(r => (
                      <label key={r.key}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs font-medium transition-all ${
                          cancelReasonKey === r.key
                            ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                        data-testid={`cancel-reason-${r.key}`}>
                        <input type="radio" name="cancel-reason" value={r.key}
                          checked={cancelReasonKey === r.key}
                          onChange={() => setCancelReasonKey(r.key)}
                          className="accent-indigo-600" />
                        {r.label}
                      </label>
                    ))}
                  </div>
                  {cancelReasonKey && (
                    <textarea
                      value={cancelReasonNote}
                      onChange={(e) => setCancelReasonNote(e.target.value.slice(0, 300))}
                      placeholder="Vuoi aggiungere qualcosa? (opzionale, max 300 caratteri)"
                      className="mt-2 w-full text-xs px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 resize-none"
                      rows={2}
                      data-testid="cancel-reason-note"
                    />
                  )}
                </div>
              </div>
            )}
            </div>{/* fine corpo scrollabile */}

            {/* Footer fisso — sempre visibile */}
            <div className="px-6 py-4 flex-shrink-0 border-t border-slate-100">
              {!cancelPreview ? null : !cancelPreview.can_cancel ? (
                <Button onClick={() => setCancelOpen(false)} className="w-full">Chiudi</Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCancelOpen(false)} className="flex-1" data-testid="cancel-modal-back">
                    {cancelPreview.in_trial ? "Annulla" : "Mantieni attivo"}
                  </Button>
                  <Button onClick={confirmCancel} disabled={cancelLoading || !cancelReasonKey}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50" data-testid="cancel-confirm-btn">
                    {cancelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> :
                      cancelPreview.in_trial ? "Conferma disdetta" :
                      cancelMode === "subito" ?
                        (cancelPreview.in_contratto && cancelPreview.mesi_da_saldare > 0 ? `Paga ${cancelPreview.importo_residuo_subito}€ e disdici` : "Disdici e disattiva") :
                        "Disattiva rinnovo"}
                  </Button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default AbbonamentoPage;
